import React, { useState, useEffect } from 'react';
import {
  Users, Building, MessageSquare, BarChart3, Settings, Shield,
  CheckCircle, XCircle, Clock, Eye, EyeOff, Edit, Trash2, Search,
  Filter, MoreVertical, Download, AlertTriangle, Activity,
  UserCheck, UserX, FileText, Calendar, ChevronDown, Briefcase, Award, DollarSign
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import CandidatePortalDashboard from '../candidate/CandidatePortalDashboard';
import ClientPortalDashboard from '../client/ClientPortalDashboard';

const AdminPortalDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewAs, setViewAs] = useState('admin');
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateDetail, setShowCandidateDetail] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);

  const handleViewCandidateDetail = async (candidateId) => {
    try {
      setLoading(true);
      const response = await adminAPI.getCandidate(candidateId);
      setSelectedCandidate(response.data.data);
      setShowCandidateDetail(true);
    } catch (error) {
      showMessage('Failed to load candidate details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCandidateDetail = () => {
    setSelectedCandidate(null);
    setShowCandidateDetail(false);
  };

  const handleViewClientDetail = async (clientId) => {
    try {
      setLoading(true);
      const response = await adminAPI.getClient(clientId);
      setSelectedClient(response.data.data);
      setShowClientDetail(true);
    } catch (error) {
      showMessage('Failed to load client details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseClientDetail = () => {
    setSelectedClient(null);
    setShowClientDetail(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (viewDropdownOpen && !event.target.closest('.view-dropdown')) {
        setViewDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [viewDropdownOpen]);

  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(message);
      setSuccess('');
      setTimeout(() => setError(''), 5000);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'candidates', label: 'Candidates', icon: Users },
    { id: 'clients', label: 'Clients', icon: Building },
    { id: 'requests', label: 'Info Requests', icon: MessageSquare },
    { id: 'audit', label: 'Audit Log', icon: Activity },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const viewOptions = [
    { value: 'admin', label: 'Admin View', icon: Shield },
    { value: 'client', label: 'Client View', icon: Building },
    { value: 'candidate', label: 'Candidate View', icon: Users }
  ];

  // Render different portal based on viewAs selection with admin overlay
  if (viewAs === 'client') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Admin Header Overlay */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <img
                    src="/appsytec-logo.png"
                    alt="Appsytec Logo"
                    className="h-8 w-auto object-contain mr-4"
                  />
                  <div className="flex items-center">
                    <Shield className="h-6 w-6 text-indigo-600 mr-2" />
                    <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
                  </div>
                </div>

                {/* View As Dropdown */}
                <div className="relative view-dropdown">
                  <button
                    onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="text-xs font-medium text-gray-500">VIEW AS:</span>
                    {React.createElement(viewOptions.find(opt => opt.value === viewAs)?.icon || Shield, {
                      className: "h-4 w-4"
                    })}
                    <span>{viewOptions.find(opt => opt.value === viewAs)?.label}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {viewDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1">
                        {viewOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setViewAs(option.value);
                              setViewDropdownOpen(false);
                            }}
                            className={`flex items-center w-full px-4 py-2 text-sm ${
                              viewAs === option.value
                                ? 'bg-indigo-100 text-indigo-900'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {React.createElement(option.icon, { className: "h-4 w-4 mr-3" })}
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  Welcome, {user.email}
                </span>
                <button
                  onClick={logout}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Client Portal Content */}
        <div className="pt-0">
          <ClientPortalDashboard />
        </div>
      </div>
    );
  }

  if (viewAs === 'candidate') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Admin Header Overlay */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <img
                    src="/appsytec-logo.png"
                    alt="Appsytec Logo"
                    className="h-8 w-auto object-contain mr-4"
                  />
                  <div className="flex items-center">
                    <Shield className="h-6 w-6 text-indigo-600 mr-2" />
                    <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
                  </div>
                </div>

                {/* View As Dropdown */}
                <div className="relative view-dropdown">
                  <button
                    onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="text-xs font-medium text-gray-500">VIEW AS:</span>
                    {React.createElement(viewOptions.find(opt => opt.value === viewAs)?.icon || Shield, {
                      className: "h-4 w-4"
                    })}
                    <span>{viewOptions.find(opt => opt.value === viewAs)?.label}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {viewDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1">
                        {viewOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setViewAs(option.value);
                              setViewDropdownOpen(false);
                            }}
                            className={`flex items-center w-full px-4 py-2 text-sm ${
                              viewAs === option.value
                                ? 'bg-indigo-100 text-indigo-900'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {React.createElement(option.icon, { className: "h-4 w-4 mr-3" })}
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  Welcome, {user.email}
                </span>
                <button
                  onClick={logout}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Candidate Portal Content */}
        <div className="pt-0">
          <CandidatePortalDashboard />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <img
                  src="/appsytec-logo.png"
                  alt="Appsytec Logo"
                  className="h-8 w-auto object-contain mr-4"
                />
                <div className="flex items-center">
                  <Shield className="h-6 w-6 text-indigo-600 mr-2" />
                  <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
                </div>
              </div>

              {/* View As Dropdown */}
              <div className="relative view-dropdown">
                <button
                  onClick={() => setViewDropdownOpen(!viewDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="text-xs font-medium text-gray-500">VIEW AS:</span>
                  {React.createElement(viewOptions.find(opt => opt.value === viewAs)?.icon || Shield, {
                    className: "h-4 w-4"
                  })}
                  <span>{viewOptions.find(opt => opt.value === viewAs)?.label}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {viewDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      {viewOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setViewAs(option.value);
                            setViewDropdownOpen(false);
                          }}
                          className={`flex items-center w-full px-4 py-2 text-sm ${
                            viewAs === option.value
                              ? 'bg-indigo-100 text-indigo-900'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {React.createElement(option.icon, { className: "h-4 w-4 mr-3" })}
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user.email}
              </span>
              <button
                onClick={logout}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-white rounded-lg shadow-sm p-6 h-fit">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                      activeTab === tab.id
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 ml-8">
            <div className="bg-white rounded-lg shadow-sm">
              {activeTab === 'dashboard' && (
                <DashboardTab showMessage={showMessage} />
              )}
              {activeTab === 'candidates' && (
                <CandidatesTab showMessage={showMessage} onViewDetails={handleViewCandidateDetail} />
              )}
              {activeTab === 'clients' && (
                <ClientsTab showMessage={showMessage} onViewDetails={handleViewClientDetail} />
              )}
              {activeTab === 'requests' && (
                <RequestsTab showMessage={showMessage} />
              )}
              {activeTab === 'audit' && (
                <AuditTab showMessage={showMessage} />
              )}
              {activeTab === 'settings' && (
                <SettingsTab showMessage={showMessage} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Candidate Detail Modal */}
      {showCandidateDetail && selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          onClose={handleCloseCandidateDetail}
          showMessage={showMessage}
        />
      )}

      {/* Client Detail Modal */}
      {showClientDetail && selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          onClose={handleCloseClientDetail}
          showMessage={showMessage}
        />
      )}
    </div>
  );
};

const DashboardTab = ({ showMessage }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboard();
      setDashboardData(response.data.data);
    } catch (error) {
      showMessage('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Dashboard Overview</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-blue-50 p-6 rounded-lg">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-blue-600">Total Candidates</p>
              <p className="text-2xl font-bold text-blue-900">
                {dashboardData?.candidates?.total_candidates || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg">
          <div className="flex items-center">
            <UserCheck className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-green-600">Approved Candidates</p>
              <p className="text-2xl font-bold text-green-900">
                {dashboardData?.candidates?.approved_candidates || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 p-6 rounded-lg">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-yellow-600">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-900">
                {dashboardData?.candidates?.pending_candidates || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-6 rounded-lg">
          <div className="flex items-center">
            <Building className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-purple-600">Active Clients</p>
              <p className="text-2xl font-bold text-purple-900">
                {dashboardData?.clients?.total_clients || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {dashboardData?.recent_activity?.map((activity, index) => (
              <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <Activity className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(activity.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Information Requests</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Pending</span>
              <span className="text-lg font-semibold text-yellow-600">
                {dashboardData?.requests?.pending_requests || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Approved</span>
              <span className="text-lg font-semibold text-green-600">
                {dashboardData?.requests?.approved_requests || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Rejected</span>
              <span className="text-lg font-semibold text-red-600">
                {dashboardData?.requests?.rejected_requests || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CandidatesTab = ({ showMessage, onViewDetails }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 20,
    status: 'all',
    search: ''
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    loadCandidates();
  }, [searchParams]);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCandidates(searchParams);
      setCandidates(response.data.data.candidates);
      setPagination(response.data.data.pagination);
    } catch (error) {
      showMessage('Failed to load candidates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateCandidateApproval = async (candidateId, approved, visibility = null) => {
    try {
      await adminAPI.updateCandidateApproval(candidateId, { approved, visibility });
      showMessage(`Candidate ${approved ? 'approved' : 'rejected'} successfully`);
      loadCandidates();
    } catch (error) {
      showMessage('Failed to update candidate approval', 'error');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Candidate Management</h2>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search candidates..."
              value={searchParams.search}
              onChange={(e) => setSearchParams({ ...searchParams, search: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <select
              value={searchParams.status}
              onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
            </select>
          </div>
          <div>
            <select
              value={searchParams.limit}
              onChange={(e) => setSearchParams({ ...searchParams, limit: parseInt(e.target.value), page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Candidates Table */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clearance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {candidates.map((candidate) => (
                <tr key={candidate.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {candidate.first_name} {candidate.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{candidate.email}</div>
                      {candidate.current_job_title && (
                        <div className="text-sm text-gray-500">{candidate.current_job_title}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {candidate.location || 'Not specified'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {candidate.security_clearance || 'None'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      candidate.profile_approved
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {candidate.profile_approved ? 'Approved' : 'Pending'}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">
                      Visibility: {candidate.visibility}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(candidate.user_created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {!candidate.profile_approved && (
                        <button
                          onClick={() => updateCandidateApproval(candidate.id, true, 'searchable')}
                          className="text-green-600 hover:text-green-900"
                          title="Approve"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      )}
                      {candidate.profile_approved && (
                        <button
                          onClick={() => updateCandidateApproval(candidate.id, false, 'private')}
                          className="text-red-600 hover:text-red-900"
                          title="Reject"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onViewDetails(candidate.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {candidates.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2">No candidates found</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-1">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setSearchParams({ ...searchParams, page })}
                    className={`px-3 py-1 text-sm rounded ${
                      page === pagination.page
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ClientsTab = ({ showMessage, onViewDetails }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getClients();
      setClients(response.data.data.clients || []);
    } catch (error) {
      showMessage('Failed to load clients', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Client Management</h2>
        <span className="text-sm text-gray-500">{clients.length} total clients</span>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {clients.map((client) => (
            <li key={client.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Building className="h-10 w-10 text-gray-400" />
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-gray-900">{client.company_name}</p>
                      {client.is_premium && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Premium
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{client.contact_person}</p>
                    <p className="text-xs text-gray-400">{client.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <p className="text-sm text-gray-900">{client.search_credits} credits</p>
                    <p className="text-xs text-gray-500">Industry: {client.industry}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-900">{client.company_size}</p>
                    <p className="text-xs text-gray-500">
                      Last login: {client.last_login ? new Date(client.last_login).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                  <button
                    onClick={() => onViewDetails(client.id)}
                    className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const RequestsTab = ({ showMessage }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 20,
    status: 'pending'
  });

  useEffect(() => {
    loadRequests();
  }, [searchParams]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getInformationRequests(searchParams);
      setRequests(response.data.data.requests);
    } catch (error) {
      showMessage('Failed to load requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const respondToRequest = async (requestId, status, response = '') => {
    try {
      await adminAPI.respondToRequest(requestId, { status, admin_response: response });
      showMessage(`Request ${status} successfully`);
      loadRequests();
    } catch (error) {
      showMessage('Failed to respond to request', 'error');
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Information Requests</h2>

      {/* Status Filter */}
      <div className="mb-6">
        <select
          value={searchParams.status}
          onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value, page: 1 })}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="pending">Pending Requests</option>
          <option value="approved">Approved Requests</option>
          <option value="rejected">Rejected Requests</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div key={request.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="font-medium text-gray-900">
                      {request.company_name} → {request.first_name} {request.last_name}
                    </h3>
                    <span className={`ml-2 px-2 py-1 text-xs rounded ${
                      request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      request.status === 'approved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {request.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{request.current_job_title}</p>
                  <p className="text-sm text-gray-500">
                    Requested on {new Date(request.requested_at).toLocaleDateString()}
                  </p>

                  {request.request_message && (
                    <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
                      <p className="font-medium text-gray-700">Client message:</p>
                      <p className="text-gray-600">{request.request_message}</p>
                    </div>
                  )}

                  {request.admin_response && (
                    <div className="mt-2 p-3 bg-blue-50 rounded text-sm">
                      <p className="font-medium text-blue-700">Your response:</p>
                      <p className="text-blue-600">{request.admin_response}</p>
                    </div>
                  )}
                </div>

                {request.status === 'pending' && (
                  <div className="ml-4 flex space-x-2">
                    <button
                      onClick={() => respondToRequest(request.id, 'approved', 'Information request approved')}
                      className="px-3 py-1 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => respondToRequest(request.id, 'rejected', 'Information request rejected')}
                      className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {requests.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2">No information requests found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AuditTab = ({ showMessage }) => {
  // Implementation for audit log
  return <div className="p-6">Audit log coming soon...</div>;
};

const SettingsTab = ({ showMessage }) => {
  const [fieldSettings, setFieldSettings] = useState({});
  const [groupedSettings, setGroupedSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});

  useEffect(() => {
    loadFieldVisibilitySettings();
  }, []);

  const loadFieldVisibilitySettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFieldVisibilitySettings();
      const { settings, grouped } = response.data.data;

      // Convert to a map for easier access
      const settingsMap = {};
      settings.forEach(setting => {
        settingsMap[setting.id] = setting;
      });

      setFieldSettings(settingsMap);
      setGroupedSettings(grouped);
    } catch (error) {
      showMessage('Failed to load field visibility settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldToggle = (fieldId, isVisible) => {
    setFieldSettings(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        is_visible_to_clients: isVisible
      }
    }));

    // Track pending changes
    setPendingChanges(prev => ({
      ...prev,
      [fieldId]: isVisible
    }));
  };

  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      showMessage('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const updates = Object.entries(pendingChanges).map(([fieldId, isVisible]) => ({
        field_id: parseInt(fieldId),
        is_visible_to_clients: isVisible
      }));

      const result = await adminAPI.bulkUpdateFieldVisibilitySettings(updates);

      // Check if all updates succeeded
      const failed = result.data.results.filter(r => !r.success);
      const succeeded = result.data.results.filter(r => r.success);

      if (failed.length === 0) {
        showMessage(`Updated visibility for ${succeeded.length} field(s) successfully`);
        setPendingChanges({});
        await loadFieldVisibilitySettings();
      } else if (succeeded.length > 0) {
        showMessage(`Updated ${succeeded.length} field(s), ${failed.length} failed. Please try again.`, 'warning');
        await loadFieldVisibilitySettings();
      } else {
        showMessage('Failed to save field visibility settings', 'error');
      }
    } catch (error) {
      showMessage('Failed to save field visibility settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setPendingChanges({});
    // Revert UI changes
    setFieldSettings(prev => {
      const reverted = { ...prev };
      Object.keys(pendingChanges).forEach(fieldId => {
        // Revert to original value by reloading
        loadFieldVisibilitySettings();
      });
      return reverted;
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'personal': return Users;
      case 'contact': return MessageSquare;
      case 'professional': return Briefcase;
      case 'salary': return DollarSign;
      case 'qualifications': return Award;
      case 'experience': return Clock;
      case 'profile': return FileText;
      case 'documents': return Download;
      default: return Settings;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'personal': return 'bg-blue-50 text-blue-800';
      case 'contact': return 'bg-purple-50 text-purple-800';
      case 'professional': return 'bg-green-50 text-green-800';
      case 'salary': return 'bg-yellow-50 text-yellow-800';
      case 'qualifications': return 'bg-indigo-50 text-indigo-800';
      case 'experience': return 'bg-orange-50 text-orange-800';
      case 'profile': return 'bg-pink-50 text-pink-800';
      case 'documents': return 'bg-gray-50 text-gray-800';
      default: return 'bg-gray-50 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Field Visibility Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Control which candidate fields are visible to clients during profile viewing
          </p>
        </div>

        {Object.keys(pendingChanges).length > 0 && (
          <div className="flex space-x-3">
            <button
              onClick={discardChanges}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Discard Changes
            </button>
            <button
              onClick={saveChanges}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : `Save ${Object.keys(pendingChanges).length} Change(s)`}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {Object.entries(groupedSettings).map(([category, fields]) => {
          const IconComponent = getCategoryIcon(category);
          const colorClass = getCategoryColor(category);

          return (
            <div key={category} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                    <IconComponent className="h-4 w-4 mr-1" />
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </span>
                  <span className="ml-3 text-sm text-gray-500">
                    {fields.length} field{fields.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {fields.map((field) => {
                  const currentField = fieldSettings[field.id];
                  const isVisible = currentField?.is_visible_to_clients ?? false;
                  const hasChanges = pendingChanges.hasOwnProperty(field.id);

                  return (
                    <div key={field.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h3 className="text-sm font-medium text-gray-900">{field.field_label}</h3>
                          {hasChanges && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                              Modified
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{field.field_description}</p>
                      </div>

                      <div className="flex items-center ml-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isVisible}
                            onChange={(e) => handleFieldToggle(field.id, e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`relative inline-flex h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${
                            isVisible ? 'bg-indigo-600' : 'bg-gray-200'
                          }`}>
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                isVisible ? 'translate-x-6' : 'translate-x-1'
                              } mt-1`}
                            />
                          </div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            {isVisible ? 'Visible' : 'Hidden'}
                          </span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Important Notes</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Visible fields</strong> are shown to clients when they view candidate profiles</li>
                <li><strong>Hidden fields</strong> are replaced with "[REDACTED]" or omitted entirely</li>
                <li>Contact information (name, email, phone) is typically hidden until information requests are approved</li>
                <li>Changes affect all future candidate profile views by clients immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDetailModal = ({ client, onClose, showMessage }) => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (client) {
      setEditData({
        company_name: client.company_name || '',
        contact_person: client.contact_person || '',
        phone: client.phone || '',
        industry: client.industry || '',
        company_size: client.company_size || '',
        is_premium: client.is_premium || false,
        search_credits: client.search_credits || 0,
        subscription_expires: client.subscription_expires ? client.subscription_expires.split('T')[0] : ''
      });
    }
  }, [client]);

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await adminAPI.updateClient(client.id, editData);
      showMessage('Client updated successfully');
      setEditing(false);
      onClose();
    } catch (error) {
      showMessage('Failed to update client', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      company_name: client.company_name || '',
      contact_person: client.contact_person || '',
      phone: client.phone || '',
      industry: client.industry || '',
      company_size: client.company_size || '',
      is_premium: client.is_premium || false,
      search_credits: client.search_credits || 0,
      subscription_expires: client.subscription_expires ? client.subscription_expires.split('T')[0] : ''
    });
    setEditing(false);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-3 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Client Details {editing ? '- Edit Mode' : ''}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Client Information */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              {editing ? (
                <input
                  type="text"
                  value={editData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {client.company_name}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person
              </label>
              {editing ? (
                <input
                  type="text"
                  value={editData.contact_person}
                  onChange={(e) => handleInputChange('contact_person', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {client.contact_person}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {client.email}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              {editing ? (
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {client.phone || 'Not provided'}
                </p>
              )}
            </div>
          </div>

          {/* Company Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry
              </label>
              {editing ? (
                <input
                  type="text"
                  value={editData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {client.industry || 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Size
              </label>
              {editing ? (
                <select
                  value={editData.company_size}
                  onChange={(e) => handleInputChange('company_size', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select size</option>
                  <option value="Startup (1-10)">Startup (1-10)</option>
                  <option value="Small (11-50)">Small (11-50)</option>
                  <option value="Medium (51-200)">Medium (51-200)</option>
                  <option value="Large (201-1000)">Large (201-1000)</option>
                  <option value="Enterprise (1000+)">Enterprise (1000+)</option>
                </select>
              ) : (
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {client.company_size || 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Credits
              </label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  value={editData.search_credits}
                  onChange={(e) => handleInputChange('search_credits', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {client.search_credits || 0}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Premium Status
              </label>
              {editing ? (
                <select
                  value={editData.is_premium}
                  onChange={(e) => handleInputChange('is_premium', e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value={false}>Standard</option>
                  <option value={true}>Premium</option>
                </select>
              ) : (
                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                  client.is_premium
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {client.is_premium ? 'Premium' : 'Standard'}
                </span>
              )}
            </div>
          </div>

          {/* Subscription Information */}
          {client.is_premium && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription Expires
                </label>
                {editing ? (
                  <input
                    type="date"
                    value={editData.subscription_expires}
                    onChange={(e) => handleInputChange('subscription_expires', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                ) : (
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {client.subscription_expires ? new Date(client.subscription_expires).toLocaleDateString() : 'Not set'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Activity Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Searches
              </label>
              <p className="text-lg font-semibold text-gray-900">
                {client.total_searches || 0}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Candidate Views
              </label>
              <p className="text-lg font-semibold text-gray-900">
                {client.total_views || 0}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Information Requests
              </label>
              <p className="text-lg font-semibold text-gray-900">
                {client.total_requests || 0}
              </p>
            </div>
          </div>

          {/* Account Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Created
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {new Date(client.user_created_at).toLocaleDateString()}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Login
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {client.last_login ? new Date(client.last_login).toLocaleDateString() : 'Never'}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            disabled={loading}
          >
            Close
          </button>

          {editing ? (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Edit Client
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CandidateDetailModal = ({ candidate, onClose, showMessage }) => {
  const [loading, setLoading] = useState(false);

  const updateCandidateApproval = async (approved, visibility = null) => {
    try {
      setLoading(true);
      await adminAPI.updateCandidateApproval(candidate.id, { approved, visibility });
      showMessage(`Candidate ${approved ? 'approved' : 'rejected'} successfully`);
      onClose();
    } catch (error) {
      showMessage('Failed to update candidate approval', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-3 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Candidate Details - Review
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Candidate Information */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.first_name} {candidate.last_name}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.email}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.phone || 'Not provided'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.location || 'Not provided'}
              </p>
            </div>
          </div>

          {/* Professional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.job_title || 'Not provided'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Experience Years
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.experience_years || 'Not provided'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Salary
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.current_salary ? `£${candidate.current_salary.toLocaleString()}` : 'Not provided'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Salary
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.expected_salary ? `£${candidate.expected_salary.toLocaleString()}` : 'Not provided'}
              </p>
            </div>
          </div>

          {/* Profile Summary */}
          {candidate.profile_summary && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Profile Summary
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                {candidate.profile_summary}
              </p>
            </div>
          )}

          {/* Skills */}
          {candidate.skills && candidate.skills.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Skills
              </label>
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(candidate.skills) ? candidate.skills : JSON.parse(candidate.skills || '[]')).map((skill, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Technologies */}
          {candidate.technologies && candidate.technologies.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Technologies
              </label>
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(candidate.technologies) ? candidate.technologies : JSON.parse(candidate.technologies || '[]')).map((tech, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Employment History */}
          {candidate.employment_history && candidate.employment_history.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Employment History
              </label>
              <div className="space-y-4">
                {candidate.employment_history.map((job, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">{job.job_title}</h4>
                        <p className="text-sm text-gray-700 font-medium">{job.employer}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {job.start_date_month && job.start_date_year
                            ? `${String(job.start_date_month).padStart(2, '0')}/${job.start_date_year}`
                            : 'Start date not specified'}
                          {' - '}
                          {job.is_current
                            ? 'Present'
                            : (job.end_date_month && job.end_date_year
                                ? `${String(job.end_date_month).padStart(2, '0')}/${job.end_date_year}`
                                : 'End date not specified')}
                        </p>
                        {job.is_current && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full mt-1">
                            Current Role
                          </span>
                        )}
                      </div>
                    </div>

                    {job.responsibilities && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-gray-600 mb-1">Responsibilities:</p>
                        <p className="text-xs text-gray-700">{job.responsibilities}</p>
                      </div>
                    )}

                    {job.technology_stack && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-gray-600 mb-1">Technology Stack:</p>
                        <p className="text-xs text-gray-700">{job.technology_stack}</p>
                      </div>
                    )}

                    {job.achievements && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Key Achievements:</p>
                        <p className="text-xs text-gray-700">{job.achievements}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Status
              </label>
              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                candidate.profile_approved
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {candidate.profile_approved ? 'Approved' : 'Pending Review'}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibility
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.visibility || 'private'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Availability
              </label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {candidate.availability || 'Not specified'}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            disabled={loading}
          >
            Close
          </button>

          {!candidate.profile_approved && (
            <>
              <button
                onClick={() => updateCandidateApproval(false, 'private')}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Reject'}
              </button>
              <button
                onClick={() => updateCandidateApproval(true, 'searchable')}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Approve'}
              </button>
            </>
          )}

          {candidate.profile_approved && (
            <button
              onClick={() => updateCandidateApproval(false, 'private')}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Revoke Approval'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPortalDashboard;